@file:Suppress("UnsafeCastFromDynamic")

package com.zi.support.web

import com.zi.support.shared.KanbanPolicy
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.Date
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

internal object KanbanScreen {
    private var projectStop: dynamic = null
    private var taskStop: dynamic = null
    private var projects: List<WebRecord> = emptyList()
    private var tasks: List<WebRecord> = emptyList()
    private var users: List<WebRecord> = emptyList()
    private var openProjectId: String? = null
    private var filterUid = ""
    /** The Tasks view's project filter, blank for every project. */
    private var filterProjectId = ""
    /** A project or task status, or blank for every status; see [KanbanPolicy.shownStatuses]. */
    private var filterStatus = ""
    private var query = ""
    private var boardMode = "projects"
    private var projectsLoaded = false
    private var tasksLoaded = false
    private var tickHandle: Int? = null

    fun stop() {
        cancelListener(projectStop); cancelListener(taskStop); projectStop = null; taskStop = null; openProjectId = null
        tickHandle?.let { window.clearInterval(it) }; tickHandle = null
    }

    fun render() {
        stop()
        projectsLoaded = false; tasksLoaded = false; lastView = null
        element("content")?.innerHTML = "<div id=\"kanban-root\"><div class=\"empty\">Loading Kanban…</div></div>"
        promise(FirebaseGateway.getCollection("supportUsers"), { snapshot ->
            val docs = snapshot.docs
            users = (0 until (docs.length as Int)).map { WebRecord(docs[it].id as String, docs[it].ref.path as String, docs[it].data()) }
                .filter { field(it.data, "uid").isNotBlank() }.sortedBy { field(it.data, "username").lowercase() }
        })
        val fs = FirebaseGateway.firestore
        projectStop = FirebaseGateway.listenCollection(KanbanPolicy.PROJECTS, arrayOf(fs.limit(500)),
            { projects = it.sortedByDescending { r -> timestampMillis(r.data.updatedAt) }; projectsLoaded = true; draw() }, ::showError)
        // ponytail: one collectionGroup listener feeds both the board progress bars and the open project; paginate if tasks pass a few thousand.
        taskStop = FirebaseGateway.listenQuery(fs.query(fs.collectionGroup(FirebaseGateway.db, KanbanPolicy.TASKS), fs.limit(5000)),
            { tasks = it.sortedByDescending { r -> timestampMillis(r.data.updatedAt) }; tasksLoaded = true; draw() }, ::showError)
        tickHandle = window.setInterval({ tickTimers() }, 1000)
    }

    /** Running task timers tick in place each second instead of redrawing the board. */
    private fun tickTimers() {
        val nodes = document.querySelectorAll("[data-kb-timer-start]")
        for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let { node ->
            node.querySelector("b")?.textContent = timerText(node.getAttribute("data-kb-timer-start")!!.toDouble(), null, node.getAttribute("data-kb-timer-banked")?.toDoubleOrNull() ?: 0.0)
        }
    }

    private fun timerText(start: Double, stop: Double?, banked: Double) = KanbanPolicy.formatTimer(banked.toLong() +
        KanbanPolicy.workingMillis(start.toLong(), (stop ?: Date.now()).toLong(), -Date().getTimezoneOffset().toInt()))

    /** Time from earlier in-progress spells, banked when a paused task resumes. */
    private fun banked(d: dynamic): Double { val ms = d?.timerBankedMs; return if (ms is Number) ms.toDouble() else 0.0 }

    private fun timer(d: dynamic): String {
        val start = timestampMillis(d.timerStartedAt).takeIf { it > 0 } ?: return ""
        val stop = timestampMillis(d.timerStoppedAt).takeIf { it > 0 }
        val running = stop == null && field(d, "status") == "in_progress"
        return "<span class=\"kb-timer${if (running) " kb-timer-live" else ""}\"${if (running) " data-kb-timer-start=\"$start\" data-kb-timer-banked=\"${banked(d)}\"" else ""} title=\"Time in progress, 07:00–17:00 on working days only\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">timer</span><b>${timerText(start, stop, banked(d))}</b></span>"
    }

    /** Adds the timer fields when a task's status move starts, resumes or stops its timer; [old] is the task's current data. */
    private fun withTimer(data: dynamic, old: dynamic, from: String, to: String): dynamic {
        when (KanbanPolicy.timerChange(from, to)) {
            "start" -> { data.timerStartedAt = FirebaseGateway.serverTimestamp(); data.timerStoppedAt = null; data.timerBankedMs = 0 }
            "resume" -> {
                val start = timestampMillis(old?.timerStartedAt)
                val stop = timestampMillis(old?.timerStoppedAt).takeIf { it > 0 } ?: Date.now()
                data.timerBankedMs = banked(old) + if (start > 0) KanbanPolicy.workingMillis(start.toLong(), stop.toLong(), -Date().getTimezoneOffset().toInt()).toDouble() else 0.0
                data.timerStartedAt = FirebaseGateway.serverTimestamp(); data.timerStoppedAt = null
            }
            "stop" -> data.timerStoppedAt = FirebaseGateway.serverTimestamp()
        }
        // When and by whom it was finished, for the Kanban tasks leaderboard.
        if (to == "done" && from != "done") { data.completedAt = FirebaseGateway.serverTimestamp(); data.completedByUid = FirebaseGateway.currentUser()?.uid; data.completedByName = WebApp.currentName }
        return data
    }

    private fun showError(message: String) { element("kanban-root")?.innerHTML = "<div class=\"empty\">${escape(message)}</div>" }

    private var lastView: String? = null
    private var landedId: String? = null

    private fun draw() {
        val root = element("kanban-root") ?: return
        if (!projectsLoaded || !tasksLoaded) return
        val project = openProjectId?.let { id -> projects.firstOrNull { it.id == id } }
        if (openProjectId != null && project == null) openProjectId = null
        // Cards animate in only when the view changes, not on every live snapshot.
        val view = project?.id ?: boardMode
        root.className = if (view != lastView) "kb-enter" else ""
        lastView = view
        // The board is redrawn as HTML, so keep the caret in the search box across keystrokes and live updates.
        val searching = document.activeElement?.id == "kanban-search"
        root.innerHTML = when { project != null -> projectHtml(project); boardMode == "tasks" -> allTasksHtml(); else -> boardHtml() }
        landedId = null
        if (project == null) {
            document.querySelectorAll("[data-kanban-mode]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let { node ->
                node.addEventListener("click", { boardMode = node.getAttribute("data-kanban-mode").orEmpty(); draw() })
            } }
            bindFilterChips()
            click("kanban-clear-filters") { filterUid = ""; filterStatus = ""; filterProjectId = ""; query = ""; draw() }
            (element("kanban-search") as? HTMLInputElement)?.let { box ->
                box.addEventListener("input", { query = box.value; draw() })
                if (searching) { box.focus(); box.setSelectionRange(box.value.length, box.value.length) }
            }
        }
        if (project == null && boardMode == "tasks") {
            click("kanban-new-task") { editTask(null, null) }
            bindCards("data-kanban-task") { id -> tasks.firstOrNull { it.id == id }?.let { task -> projectOf(task)?.let { editTask(it, task) } } }
            bindDrops { id, status -> tasks.firstOrNull { it.id == id }?.let { moveTask(it, status) } }
        } else if (project == null) {
            click("kanban-new-project") { editProject(null) }
            bindCards("data-kanban-project") { id -> openProjectId = id; draw() }
            bindDrops { id, status -> update("${KanbanPolicy.PROJECTS}/$id", id, status) }
        } else {
            click("kanban-back") { openProjectId = null; draw() }
            click("kanban-edit-project") { editProject(project) }
            click("kanban-new-task") { editTask(project, null) }
            bindCards("data-kanban-task") { id -> tasks.firstOrNull { it.id == id }?.let { editTask(project, it) } }
            bindDrops { id, status -> tasks.firstOrNull { it.id == id }?.let { moveTask(it, status) } }
        }
    }

    private fun moveTask(task: WebRecord, status: String) {
        val from = field(task.data, "status")
        if (from == status) return
        landedId = task.id
        promise(FirebaseGateway.update(task.path, withTimer(json("status" to status, "updatedAt" to FirebaseGateway.serverTimestamp()), task.data, from, status)), {})
    }

    private fun update(path: String, id: String, status: String) {
        landedId = id
        promise(FirebaseGateway.update(path, json("status" to status, "updatedAt" to FirebaseGateway.serverTimestamp())), {})
    }

    private fun projectOf(task: WebRecord) = projects.firstOrNull { it.id == field(task.data, "projectId") }
    private fun tasksOf(projectId: String) = tasks.filter { field(it.data, "projectId") == projectId }
    private fun isDone(task: WebRecord) = field(task.data, "status") == "done"
    private fun plural(n: Int, word: String) = "$n $word${if (n == 1) "" else "s"}"

    /** Project assignees: the `assignees` list, or the single legacy assignedTo fields on older projects. */
    private fun assigneesOf(d: dynamic): List<Pair<String, String>> {
        val list = d?.assignees
        if (list != null && list.length as Int > 0) return (0 until list.length as Int).map { field(list[it], "uid") to field(list[it], "name") }
        val uid = field(d, "assignedToUid")
        return if (uid.isBlank()) emptyList() else listOf(uid to field(d, "assignedToName"))
    }

    /** Search covers everything on a card, plus a project's tasks so a task title finds its project. */
    private fun taskFields(r: WebRecord) = listOf(field(r.data, "title"), field(r.data, "description"), field(r.data, "assignedToName"),
        projectOf(r)?.let { field(it.data, "title") }.orEmpty(), KanbanPolicy.statusLabel(field(r.data, "status")), field(r.data, "priority"))

    private fun projectFields(p: WebRecord) = listOf(field(p.data, "title"), field(p.data, "notes"), field(p.data, "company"),
        KanbanPolicy.statusLabel(field(p.data, "status")), field(p.data, "priority")) + assigneesOf(p.data).map { it.second } +
        tasksOf(p.id).flatMap { listOf(field(it.data, "title"), field(it.data, "description")) }

    private fun searchBox(noun: String) = "<div class=\"search-shell kb-search\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">search</span><label class=\"visually-hidden\" for=\"kanban-search\">Search $noun</label><input id=\"kanban-search\" class=\"input search\" type=\"search\" value=\"${escape(query)}\" placeholder=\"Search $noun…\"></div>"

    private fun boardHtml(): String {
        val shown = KanbanPolicy.shownStatuses(KanbanPolicy.projectStatuses, filterStatus)
        val base = projects.filter { p -> (filterUid.isBlank() || assigneesOf(p.data).any { it.first == filterUid }) && KanbanPolicy.matches(query, projectFields(p)) }
        val visible = base.filter { KanbanPolicy.normalizeProjectStatus(field(it.data, "status")) in shown }
        fun count(vararg statuses: String) = base.count { KanbanPolicy.normalizeProjectStatus(field(it.data, "status")) in statuses }
        val stats = statCard("Projects", base.size, "folder_open") + statCard("Not started", count("idea", "backlog"), "lightbulb", "slate", base.size) +
            statCard("In progress", count("in_progress"), "pending_actions", "blue", base.size) + statCard("Paused", count("paused"), "pause_circle", "amber", base.size) +
            statCard("Done", count("done"), "task_alt", "green", base.size)
        val columns = shown.joinToString("") { status ->
            val rows = visible.filter { KanbanPolicy.normalizeProjectStatus(field(it.data, "status")) == status }
            column(status, rows.size, rows.mapIndexed { i, r -> projectCard(r, i) }.joinToString(""), "projects")
        }
        val active = visible.count { field(it.data, "status") == "in_progress" }
        return """
          ${modeSwitch()}
          <div class="kb-head"><div><h2>Projects</h2><p>${plural(visible.size, "project")} · $active in progress · drag a card to change its status</p></div>
            <div class="kanban-actions">${searchBox("projects")}${userChip()}${statusChip(KanbanPolicy.projectStatuses)}
            <button id="kanban-new-project" class="btn btn-primary"><span class="material-symbols-rounded">add</span>New project</button></div></div>
          <div class="stats-grid kb-stats">$stats</div>
          <div class="kanban-board">$columns</div>
        """.trimIndent()
    }

    private fun modeSwitch() = "<div class=\"segmented kb-mode\" role=\"group\" aria-label=\"Board view\">" +
        listOf("projects" to "Projects", "tasks" to "Tasks").joinToString("") { (mode, label) ->
            "<button data-kanban-mode=\"$mode\" class=\"${if (mode == boardMode) "active" else ""}\" aria-pressed=\"${mode == boardMode}\">$label</button>"
        } + "</div>"

    private fun filterChip(id: String, icon: String, label: String, on: Boolean) =
        "<button type=\"button\" id=\"$id\" class=\"filter-chip${if (on) " on" else ""}\" aria-haspopup=\"dialog\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">$icon</span>" +
            "<span class=\"filter-chip-label\">${escape(label)}</span><span class=\"material-symbols-rounded filter-chip-caret\" aria-hidden=\"true\">expand_more</span></button>"
    private fun projectTitle(p: WebRecord) = field(p.data, "title").ifBlank { "Untitled project" }
    private fun projectChip() = filterChip("kanban-project-chip", "folder_open", projects.firstOrNull { it.id == filterProjectId }?.let(::projectTitle) ?: "All projects", filterProjectId.isNotBlank())
    private fun userChip() = filterChip("kanban-user-chip", "person", users.firstOrNull { field(it.data, "uid") == filterUid }?.let { field(it.data, "username") } ?: "All users", filterUid.isNotBlank())
    private fun statusChip(statuses: List<String>) = filterChip("kanban-status-chip", "flag", filterStatus.takeIf { it in statuses }?.let(KanbanPolicy::statusLabel) ?: "All statuses", filterStatus.isNotBlank())

    /** The filter chips open a picker popup (search, counts, the current choice ticked). Counts cover every card in the view. */
    private fun bindFilterChips() {
        val tasksView = boardMode == "tasks"
        val cards = if (tasksView) tasks.filter { projectOf(it) != null } else projects
        val noun = if (tasksView) "tasks" else "projects"
        click("kanban-project-chip") {
            pickOption("Filter by project", "folder_open", "All projects", projects.sortedBy { projectTitle(it).lowercase() }.map { p -> Triple(p.id, projectTitle(p), tasksOf(p.id).size) },
                filterProjectId, note = "Counts are tasks in each project", searchable = true,
                leading = { "<span class=\"filter-option-icon\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">folder</span></span>" }) { filterProjectId = it; draw() }
        }
        click("kanban-user-chip") {
            pickOption("Filter by user", "group", "All users", users.map { u -> val uid = field(u.data, "uid")
                Triple(uid, field(u.data, "username"), cards.count { c -> if (tasksView) field(c.data, "assignedToUid") == uid else assigneesOf(c.data).any { it.first == uid } }) },
                filterUid, note = "Counts are all $noun", searchable = true) { filterUid = it; draw() }
        }
        click("kanban-status-chip") {
            val statuses = if (tasksView) KanbanPolicy.taskStatuses else KanbanPolicy.projectStatuses
            val statusOf = { c: WebRecord -> if (tasksView) KanbanPolicy.normalizeTaskStatus(field(c.data, "status")) else KanbanPolicy.normalizeProjectStatus(field(c.data, "status")) }
            pickOption("Filter by status", "flag", "All statuses", statuses.map { st -> Triple(st, KanbanPolicy.statusLabel(st), cards.count { statusOf(it) == st }) },
                filterStatus, note = "Counts are all $noun", leading = { "<span class=\"filter-option-icon kb-s-$it\"><span class=\"kb-dot\"></span></span>" }) { filterStatus = it; draw() }
        }
    }

    /** Every task across every project, by task status; each card names its project. */
    private fun allTasksHtml(): String {
        val shown = KanbanPolicy.shownStatuses(KanbanPolicy.taskStatuses, filterStatus)
        if (filterProjectId.isNotBlank() && projects.none { it.id == filterProjectId }) filterProjectId = ""
        val base = tasks.filter { projectOf(it) != null && (filterProjectId.isBlank() || field(it.data, "projectId") == filterProjectId) &&
            (filterUid.isBlank() || field(it.data, "assignedToUid") == filterUid) && KanbanPolicy.matches(query, taskFields(it)) }
        val visible = base.filter { KanbanPolicy.normalizeTaskStatus(field(it.data, "status")) in shown }
        fun count(status: String) = base.count { KanbanPolicy.normalizeTaskStatus(field(it.data, "status")) == status }
        val stats = statCard("Tasks", base.size, "checklist") + statCard("To do", count("todo"), "radio_button_unchecked", "slate", base.size) +
            statCard("In progress", count("in_progress"), "pending_actions", "blue", base.size) + statCard("Paused", count("paused"), "pause_circle", "amber", base.size) +
            statCard("Review", count("review"), "rate_review", "red", base.size) + statCard("Done", count("done"), "task_alt", "green", base.size)
        val columns = shown.joinToString("") { status ->
            val rows = visible.filter { KanbanPolicy.normalizeTaskStatus(field(it.data, "status")) == status }
            column(status, rows.size, rows.mapIndexed { i, r -> taskCard(r, i, projectOf(r)?.let { field(it.data, "title") }) }.joinToString(""), "tasks")
        }
        val active = visible.count { field(it.data, "status") == "in_progress" }
        val filtered = listOf(filterProjectId, filterUid, filterStatus, query).any { it.isNotBlank() }
        return """
          ${modeSwitch()}
          <div class="kb-head"><div><h2>Tasks</h2><p>${plural(visible.size, "task")} · $active in progress · drag a card to change its status</p></div>
            <div class="kanban-actions">${searchBox("tasks")}${projectChip()}${userChip()}${statusChip(KanbanPolicy.taskStatuses)}
            <button id="kanban-clear-filters" class="btn btn-ghost"${if (filtered) "" else " disabled"}><span class="material-symbols-rounded">filter_alt_off</span>Clear filters</button>
            <button id="kanban-new-task" class="btn btn-primary"><span class="material-symbols-rounded">add</span>New task</button></div></div>
          <div class="stats-grid kb-stats">$stats</div>
          <div class="kanban-board kanban-board-tasks">$columns</div>
        """.trimIndent()
    }

    private fun projectHtml(project: WebRecord): String {
        val own = tasksOf(project.id)
        val done = own.count(::isDone)
        val percent = if (own.isEmpty()) 0 else done * 100 / own.size
        val columns = KanbanPolicy.taskStatuses.joinToString("") { status ->
            val rows = own.filter { KanbanPolicy.normalizeTaskStatus(field(it.data, "status")) == status }
            column(status, rows.size, rows.mapIndexed { i, r -> taskCard(r, i) }.joinToString(""), "tasks")
        }
        val d = project.data
        val status = KanbanPolicy.normalizeProjectStatus(field(d, "status"))
        return """
          <button id="kanban-back" class="kb-back"><span class="material-symbols-rounded">arrow_back</span>All projects</button>
          <div class="kb-head kb-project-head">
            <div class="kb-project-title">
              <div class="kb-tags"><span class="kb-status kb-s-$status">${KanbanPolicy.statusLabel(status)}</span>${priority(field(d, "priority"))}</div>
              <h2>${escape(field(d, "title"))}</h2>
              ${if (field(d, "notes").isBlank()) "" else "<p>${escape(field(d, "notes"))}</p>"}
              <div class="kb-project-meta">${assigneesOf(d).joinToString("") { avatar(it.second) }}<span>${escape(assigneesOf(d).joinToString(", ") { it.second }.ifBlank { "Unassigned" })}</span><span class="kb-sep"></span><span>${plural(own.size, "task")}</span></div>
            </div>
            <div class="kb-project-side">
              <div class="kb-summary"><strong>$percent%</strong><span>$done of ${own.size} done</span><div class="kb-bar kb-bar-lg"><i style="--w:$percent%"></i></div></div>
              <div class="kanban-actions"><button id="kanban-edit-project" class="btn"><span class="material-symbols-rounded">edit</span>Edit</button><button id="kanban-new-task" class="btn btn-primary"><span class="material-symbols-rounded">add</span>New task</button></div>
            </div>
          </div>
          <div class="kanban-board kanban-board-tasks">$columns</div>
        """.trimIndent()
    }

    private fun column(status: String, count: Int, cards: String, noun: String) = """
        <section class="kanban-column kb-s-$status" data-kanban-drop="$status">
          <header><span class="kb-dot"></span><strong>${KanbanPolicy.statusLabel(status)}</strong><span class="kb-count">$count</span></header>
          <div class="kanban-cards">${cards.ifBlank { emptyColumn(noun) }}</div>
        </section>
    """.trimIndent()

    // A small line drawing of stacked cards; stroke follows the theme via currentColor.
    private fun emptyColumn(noun: String) = """
        <div class="kb-empty"><svg viewBox="0 0 96 64" aria-hidden="true"><rect x="22" y="8" width="52" height="16" rx="5"/><rect x="14" y="24" width="68" height="20" rx="6" class="kb-empty-front"/><path d="M24 34h30M24 39h18"/><path d="M48 50v8m-4-4 4 4 4-4" class="kb-empty-arrow"/></svg><span>No $noun here</span><small>Drag a card in</small></div>
    """.trimIndent()

    private fun cardAttrs(record: WebRecord, index: Int, attribute: String) =
        """class="kanban-card${if (record.id == landedId) " kb-landed" else ""}" style="--i:${minOf(index, 12)}" draggable="true" tabindex="0" $attribute="${escape(record.id)}""""

    private fun projectCard(record: WebRecord, index: Int): String {
        val d = record.data; val own = tasksOf(record.id); val done = own.count(::isDone)
        val progress = if (own.isEmpty()) "<div class=\"kb-progress-row kb-muted\">No tasks yet</div>"
            else "<div class=\"kb-progress-row\"><div class=\"kb-bar\"><i style=\"--w:${done * 100 / own.size}%\"></i></div><span>$done/${own.size}</span></div>"
        return """<article ${cardAttrs(record, index, "data-kanban-project")}>
          <div class="kb-card-top">${priority(field(d, "priority"))}${if (field(d, "company").isBlank()) "" else "<span class=\"kb-tag\">${escape(field(d, "company"))}</span>"}</div>
          <h3>${escape(field(d, "title"))}</h3>${if (field(d, "notes").isBlank()) "" else "<p>${escape(field(d, "notes"))}</p>"}
          $progress
          <footer><span class="kb-avatars">${assigneesOf(d).joinToString("") { avatar(it.second) }}</span><small>${escape(relativeTime(d.updatedAt))}</small></footer></article>"""
    }

    private fun taskCard(record: WebRecord, index: Int, projectName: String? = null): String {
        val d = record.data
        val who = field(d, "assignedToName").ifBlank { "Unassigned" }
        val projectLine = projectName?.let { "<div class=\"kb-task-project\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">folder</span><span class=\"kb-task-project-name\">${escape(it)}</span><span class=\"kb-task-for\">· ${escape(who)}</span></div>" }.orEmpty()
        val files = attachmentsOf(d)
        val coverUrl = files.filter { KanbanPolicy.isImage(field(it, "mimeType"), field(it, "fileName")) }.map { field(it, "url") }.firstOrNull().orEmpty()
        val cover = if (coverUrl.isBlank()) "" else "<img class=\"kb-card-cover\" src=\"${escape(coverUrl)}\" alt=\"\" loading=\"lazy\" draggable=\"false\">"
        val count = if (files.isEmpty()) "" else "<span class=\"kb-tag kb-attach-count\" title=\"${plural(files.size, "attachment")}\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">attach_file</span>${files.size}</span>"
        return """<article ${cardAttrs(record, index, "data-kanban-task")}>
          $cover<div class="kb-card-top">${priority(field(d, "priority"))}${timer(d)}$count</div>
          <h3>${escape(field(d, "title"))}</h3>${if (field(d, "description").isBlank()) "" else "<p>${escape(field(d, "description"))}</p>"}
          <footer>${avatar(field(d, "assignedToName"))}<small>${escape(relativeTime(d.updatedAt))}</small></footer>$projectLine</article>"""
    }

    private fun priority(value: String): String {
        val p = KanbanPolicy.normalizePriority(value)
        return "<span class=\"kb-priority kb-p-$p\"><span class=\"kb-dot\"></span>${KanbanPolicy.priorityLabel(p)}</span>"
    }

    private fun avatar(name: String): String {
        if (name.isBlank()) return ""
        val initials = name.split(" ").filter { it.isNotBlank() }.take(2).joinToString("") { it.take(1) }.uppercase()
        return "<span class=\"kb-avatar\" style=\"background:${avatarColor(name)}\" title=\"${escape(name)}\">${escape(initials)}</span>"
    }

    private fun bindCards(attribute: String, open: (String) -> Unit) {
        val nodes = document.querySelectorAll("[$attribute]")
        for (i in 0 until nodes.length) {
            val node = nodes.item(i) as HTMLElement
            val id = node.getAttribute(attribute).orEmpty()
            node.addEventListener("click", { open(id) })
            node.addEventListener("keydown", { e -> if (e.asDynamic().key == "Enter") open(id) })
            node.addEventListener("dragstart", { e -> e.asDynamic().dataTransfer.setData("text/plain", id); node.classList.add("dragging") })
            node.addEventListener("dragend", { node.classList.remove("dragging") })
        }
    }

    private fun bindDrops(onDrop: (String, String) -> Unit) {
        val nodes = document.querySelectorAll("[data-kanban-drop]")
        for (i in 0 until nodes.length) {
            val node = nodes.item(i) as HTMLElement
            node.addEventListener("dragover", { e -> e.preventDefault(); node.classList.add("drop-target") })
            node.addEventListener("dragleave", { node.classList.remove("drop-target") })
            node.addEventListener("drop", { e ->
                e.preventDefault(); node.classList.remove("drop-target")
                val id = e.asDynamic().dataTransfer.getData("text/plain") as String
                if (id.isNotBlank()) onDrop(id, node.getAttribute("data-kanban-drop").orEmpty())
            })
        }
    }

    private fun options(values: List<String>, selected: String, label: (String) -> String) =
        values.joinToString("") { "<option value=\"$it\"${if (it == selected) " selected" else ""}>${escape(label(it))}</option>" }

    private fun userOptions(selectedUid: String) = "<option value=\"\">Unassigned</option>" + users.joinToString("") {
        val uid = field(it.data, "uid"); "<option value=\"${escape(uid)}\"${if (uid == selectedUid) " selected" else ""}>${escape(field(it.data, "username"))}</option>"
    }

    private fun assigneeRows(selected: List<String>) = users.joinToString("") {
        val uid = field(it.data, "uid")
        "<label class=\"meeting-attendee-option\"><input type=\"checkbox\" name=\"kb-assignee\" value=\"${escape(uid)}\"${if (uid in selected) " checked" else ""}><span class=\"meeting-check\"><span class=\"material-symbols-rounded\">check</span></span><span><strong>${escape(field(it.data, "username"))}</strong></span></label>"
    }

    private fun assignee(uid: String): Pair<String, String> {
        val user = users.firstOrNull { field(it.data, "uid") == uid } ?: return "" to ""
        return uid to field(user.data, "username")
    }

    private fun editProject(project: WebRecord?) {
        val d = project?.data
        modal(if (project == null) "New project" else "Edit project", """
          <div class="form-grid">
            <div class="field span-2"><label>Title</label><input id="kb-title" class="input" maxlength="300" value="${escape(field(d, "title"))}" placeholder="Project title"></div>
            <div class="field"><label>Status</label><select id="kb-status">${options(KanbanPolicy.projectStatuses, project?.let { KanbanPolicy.normalizeProjectStatus(field(d, "status")) } ?: "idea") { KanbanPolicy.statusLabel(it) }}</select></div>
            <div class="field"><label>Priority</label><select id="kb-priority">${options(KanbanPolicy.priorities, KanbanPolicy.normalizePriority(field(d, "priority"))) { KanbanPolicy.priorityLabel(it) }}</select></div>
            <div class="field span-2"><label>Assigned to</label><div class="meeting-attendee-picker">${assigneeRows(assigneesOf(d).map { it.first })}</div></div>
            <div class="field span-2"><label>Description / notes</label><textarea id="kb-notes" maxlength="5000" placeholder="Optional notes">${escape(field(d, "notes"))}</textarea></div>
          </div>
        """.trimIndent(), (if (project == null) "" else "<button id=\"kb-delete\" class=\"btn btn-danger\">Delete</button>") + "<button id=\"kb-save\" class=\"btn btn-primary\">${if (project == null) "Create project" else "Save"}</button>")
        click("kb-save") {
            val title = input("kb-title"); if (title.isBlank()) { toast("Add a project title.", true); return@click }
            val picked = document.querySelectorAll("input[name='kb-assignee']:checked").let { nodes -> (0 until nodes.length).map { assignee((nodes.item(it) as HTMLInputElement).value) } }
            // assignedTo* keeps the first assignee for older app versions that only read one.
            val data = json("title" to title, "notes" to textarea("kb-notes"), "status" to select("kb-status"), "priority" to select("kb-priority"),
                "assignees" to picked.map { json("uid" to it.first, "name" to it.second) }.toTypedArray(),
                "assignedToUid" to (picked.firstOrNull()?.first ?: ""), "assignedToName" to (picked.firstOrNull()?.second ?: ""), "updatedAt" to FirebaseGateway.serverTimestamp())
            if (project == null) {
                val user = FirebaseGateway.currentUser()
                data.company = ""; data.createdByUid = user.uid; data.createdByName = WebApp.currentName; data.createdAt = FirebaseGateway.serverTimestamp()
                // ponytail: uniqueness is checked against the loaded projects (limit 500), not a transaction.
                val id = KanbanPolicy.projectDocumentId(title, projects.map { it.id })
                promise(FirebaseGateway.set("${KanbanPolicy.PROJECTS}/$id", data), { closeModal(); toast("Project created.") })
            } else promise(FirebaseGateway.update(project.path, data), { closeModal(); toast("Project saved.") })
        }
        if (project != null) click("kb-delete") { confirmDelete("Delete ${field(d, "title")}?", "This also deletes its ${tasksOf(project.id).size} task(s).") {
            tasksOf(project.id).forEach { FirebaseGateway.delete(it.path) }
            promise(FirebaseGateway.delete(project.path), { openProjectId = null; closeModal(); toast("Project deleted."); draw() })
        } }
    }

    // Task form attachments: [keptAttachments] are already saved on the task, [pendingFiles] are picked but not uploaded yet.
    private var keptAttachments: MutableList<dynamic> = mutableListOf()
    private var pendingFiles: MutableList<File> = mutableListOf()
    private var pendingPreviews: MutableList<String> = mutableListOf()

    private fun attachmentsOf(d: dynamic): List<dynamic> {
        val list = if (d == null) null else d.attachments
        return if (list == null || !(js("Array").isArray(list) as Boolean)) emptyList() else (0 until (list.length as Int)).map { list[it] }
    }

    /** One tile: images show the picture itself, other files a named chip; [removeAttr] marks the ✕ button. */
    private fun attachmentTile(url: String, name: String, mime: String, removeAttr: String?): String {
        val body = if (KanbanPolicy.isImage(mime, name)) "<img src=\"${escape(url)}\" alt=\"${escape(name)}\">"
            else "<span class=\"material-symbols-rounded\" aria-hidden=\"true\">description</span><span class=\"kb-attach-name\">${escape(name)}</span>"
        val remove = removeAttr?.let { "<button type=\"button\" class=\"kb-attach-remove\" $it aria-label=\"Remove ${escape(name)}\">✕</button>" }.orEmpty()
        return "<div class=\"kb-attach-tile\"><a href=\"${escape(url)}\" target=\"_blank\" rel=\"noopener\" title=\"${escape(name)}\">$body</a>$remove</div>"
    }

    private fun drawFormAttachments() {
        val root = element("kb-attachments") ?: return
        root.innerHTML = keptAttachments.mapIndexed { i, a -> attachmentTile(field(a, "url"), field(a, "fileName"), field(a, "mimeType"), "data-kb-kept=\"$i\"") }.joinToString("") +
            pendingFiles.mapIndexed { i, f -> attachmentTile(pendingPreviews[i], f.name, f.type, "data-kb-pending=\"$i\"") }.joinToString("")
        fun bind(attr: String, remove: (Int) -> Unit) {
            val nodes = document.querySelectorAll("[$attr]")
            for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let { node -> node.addEventListener("click", { remove(node.getAttribute(attr)!!.toInt()); drawFormAttachments() }) }
        }
        bind("data-kb-kept") { keptAttachments.removeAt(it) }
        bind("data-kb-pending") { window.asDynamic().URL.revokeObjectURL(pendingPreviews.removeAt(it)); pendingFiles.removeAt(it) }
    }

    private fun releasePreviews() { pendingPreviews.forEach { window.asDynamic().URL.revokeObjectURL(it) }; pendingPreviews.clear(); pendingFiles.clear() }

    /** Uploads [pendingFiles] in parallel and returns their attachment records, in order. */
    private fun uploadPending(projectId: String, onProgress: (Int) -> Unit, done: (List<dynamic>) -> Unit, failed: (String) -> Unit) {
        val files = pendingFiles.toList()
        if (files.isEmpty()) { done(emptyList()); return }
        val results = arrayOfNulls<dynamic>(files.size); val progress = IntArray(files.size); var finished = 0; var stopped = false
        val batch = Date.now().toLong()
        files.forEachIndexed { i, file ->
            val path = KanbanPolicy.attachmentPath(projectId, "$batch-$i", file.name)
            promise(FirebaseGateway.uploadWithProgress(path, file) { percent -> progress[i] = percent; onProgress(progress.average().toInt()) }, { r ->
                results[i] = json("url" to field(r, "url"), "storagePath" to field(r, "path"), "fileName" to file.name, "mimeType" to file.type)
                if (++finished == files.size && !stopped) done(results.filterNotNull())
            }, { message -> if (!stopped) { stopped = true; failed(message) } })
        }
    }

    /**
     * [project] is null when a task is created from the all-tasks board; the form then asks which project it goes in.
     * Editing a task shows the same picker set to its project, so a task made in the wrong project can be moved.
     */
    private fun editTask(project: WebRecord?, task: WebRecord?) {
        val d = task?.data
        releasePreviews(); keptAttachments = attachmentsOf(d).toMutableList()
        // One dropdown with its own search box: open it, type to filter, pick a project.
        val picker = if (project != null && task == null) "" else """
            <div class="field span-2"><label id="kb-project-label" for="kb-project-toggle">Project</label>
              <div class="kb-combo"><input type="hidden" id="kb-project" value="${escape(project?.id.orEmpty())}">
                <button type="button" id="kb-project-toggle" class="input kb-combo-toggle" aria-haspopup="listbox" aria-expanded="false"><span id="kb-project-chosen">${escape(project?.let { field(it.data, "title") } ?: "Choose a project")}</span><span class="material-symbols-rounded" aria-hidden="true">expand_more</span></button>
                <div id="kb-project-pop" class="kb-combo-pop" hidden>
                  <div class="search-shell"><span class="material-symbols-rounded" aria-hidden="true">search</span><input id="kb-project-search" class="input search" type="search" placeholder="Search projects…" aria-label="Search projects"></div>
                  <div id="kb-project-list" class="kb-combo-list" role="listbox" aria-labelledby="kb-project-label"></div>
                </div>
              </div></div>"""
        modal(if (task == null) "New task" else "Edit task", """
          <div class="form-grid">
            <div class="field span-2"><label>Title</label><input id="kb-title" class="input" maxlength="300" value="${escape(field(d, "title"))}" placeholder="Task title"></div>
            $picker
            <div class="field"><label>Status</label><select id="kb-status">${options(KanbanPolicy.taskStatuses, task?.let { KanbanPolicy.normalizeTaskStatus(field(d, "status")) } ?: "todo") { KanbanPolicy.statusLabel(it) }}</select></div>
            <div class="field"><label>Priority</label><select id="kb-priority">${options(KanbanPolicy.priorities, KanbanPolicy.normalizePriority(field(d, "priority"))) { KanbanPolicy.priorityLabel(it) }}</select></div>
            <div class="field span-2"><label>Assigned to</label><select id="kb-assignee">${userOptions(field(d, "assignedToUid"))}</select></div>
            <div class="field span-2"><label>Description</label><textarea id="kb-notes" maxlength="5000" placeholder="Optional description">${escape(field(d, "description"))}</textarea></div>
            <div class="field span-2"><label>Images and files</label>
              <div id="kb-attachments" class="kb-attach-grid"></div>
              <label class="btn kb-attach-add" for="kb-files"><span class="material-symbols-rounded" aria-hidden="true">add_photo_alternate</span> Add image or file</label>
              <input id="kb-files" type="file" multiple hidden accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt">
            </div>
          </div>
        """.trimIndent(), (if (task == null) "" else "<button id=\"kb-delete\" class=\"btn btn-danger\">Delete</button>") + "<button id=\"kb-save\" class=\"btn btn-primary\">${if (task == null) "Create task" else "Save"}</button>")
        drawFormAttachments()
        if (project == null || task != null) bindProjectCombo()
        element("kb-files")?.addEventListener("change", {
            val chooser = element("kb-files") as HTMLInputElement
            val files = chooser.files
            for (i in 0 until (files?.length ?: 0)) files?.item(i)?.let { file ->
                if (keptAttachments.size + pendingFiles.size >= KanbanPolicy.MAX_ATTACHMENTS) { toast("A task holds up to ${KanbanPolicy.MAX_ATTACHMENTS} attachments.", true); return@let }
                if (file.size.toDouble() > 50 * 1024 * 1024) { toast("${file.name} is larger than 50 MB.", true); return@let }
                pendingFiles.add(file); pendingPreviews.add(window.asDynamic().URL.createObjectURL(file) as String)
            }
            chooser.value = ""
            drawFormAttachments()
        })
        click("kb-save") {
            val title = input("kb-title"); if (title.isBlank()) { toast("Add a task title.", true); return@click }
            val target = projects.firstOrNull { it.id == input("kb-project") } ?: project ?: run { toast("Pick a project.", true); return@click }
            val (uid, name) = assignee(select("kb-assignee"))
            val button = element("kb-save")
            val label = if (task == null) "Create task" else "Save"
            button?.setAttribute("disabled", "")
            fun failed(message: String) { button?.removeAttribute("disabled"); button?.textContent = label; toast(message, true) }
            uploadPending(target.id, { percent -> button?.textContent = "Uploading… $percent%" }, { uploaded ->
                val removed = attachmentsOf(d).filter { old -> keptAttachments.none { field(it, "storagePath") == field(old, "storagePath") } }
                val data = withTimer(json("title" to title, "description" to textarea("kb-notes"), "status" to select("kb-status"), "priority" to select("kb-priority"),
                    "assignedToUid" to uid, "assignedToName" to name, "attachments" to (keptAttachments + uploaded).toTypedArray(),
                    "updatedAt" to FirebaseGateway.serverTimestamp()), d, task?.let { field(d, "status") } ?: "", select("kb-status"))
                fun saved(message: String) {
                    // ponytail: only the uploader may delete a file (storage.rules), so others' removed files stay in Storage.
                    removed.forEach { old -> field(old, "storagePath").takeIf { it.isNotBlank() }?.let { path -> FirebaseGateway.deleteStorage(path).catch { _: dynamic -> null } } }
                    releasePreviews(); closeModal(); toast(message)
                }
                if (task == null) {
                    val user = FirebaseGateway.currentUser()
                    data.projectId = target.id; data.createdByUid = user.uid; data.createdByName = WebApp.currentName; data.createdAt = FirebaseGateway.serverTimestamp()
                    // Firestore confirms a write only when the server answers; the board already shows it, so don't wait.
                    promise(FirebaseGateway.add("${target.path}/${KanbanPolicy.TASKS}", data), {}); saved("Task created.")
                } else if (project != null && target.id != project.id) {
                    // Moving: the same task (id, creator, history) under the new project, and the old copy removed, in one batch.
                    val moved: dynamic = js("Object.assign({}, d, data)")
                    moved.projectId = target.id; moved.movedFromProjectId = project.id
                    promise(FirebaseGateway.move(task.path, "${target.path}/${KanbanPolicy.TASKS}/${task.id}", moved), {})
                    saved("Task moved to ${field(target.data, "title")}.")
                } else { promise(FirebaseGateway.update(task.path, data), {}); saved("Task saved.") }
            }, ::failed)
        }
        if (task != null) click("kb-delete") { confirmDelete("Delete ${field(d, "title")}?", "This task will be removed permanently.") {
            promise(FirebaseGateway.delete(task.path), { closeModal(); toast("Task deleted.") })
        } }
    }

    /** The project dropdown on a new task from the all-tasks board: toggle, type to filter, click or Enter to pick. */
    private fun bindProjectCombo() {
        val toggle = element("kb-project-toggle") ?: return
        val pop = element("kb-project-pop") ?: return
        val search = element("kb-project-search") as HTMLInputElement
        fun matches() = projects.filter { KanbanPolicy.matches(search.value, listOf(field(it.data, "title"))) }.sortedBy { field(it.data, "title").lowercase() }
        fun setOpen(open: Boolean) {
            pop.hidden = !open; toggle.setAttribute("aria-expanded", open.toString())
            if (open) { search.value = ""; drawProjectList(matches()); search.focus() }
        }
        fun pick(project: WebRecord) {
            (element("kb-project") as HTMLInputElement).value = project.id
            element("kb-project-chosen")?.textContent = field(project.data, "title")
            setOpen(false); toggle.focus()
        }
        projectPicked = { id -> projects.firstOrNull { it.id == id }?.let(::pick) }
        toggle.addEventListener("click", { setOpen(pop.hidden) })
        search.addEventListener("input", { drawProjectList(matches()) })
        search.addEventListener("keydown", { event ->
            when (event.asDynamic().key as String) {
                "Enter" -> { event.preventDefault(); matches().firstOrNull()?.let(::pick) }
                "Escape" -> { event.preventDefault(); event.stopPropagation(); setOpen(false); toggle.focus() }
            }
        })
        // A click anywhere else in the form closes the list; the form is rebuilt each time, so this goes with it.
        element("kb-project-pop")?.parentElement?.parentElement?.parentElement?.addEventListener("click", { event ->
            val target = event.target as? org.w3c.dom.Node
            if (!pop.hidden && !pop.contains(target) && !toggle.contains(target)) setOpen(false)
        })
    }

    private var projectPicked: (String) -> Unit = {}

    private fun drawProjectList(rows: List<WebRecord>) {
        val list = element("kb-project-list") ?: return
        val chosen = input("kb-project")
        list.innerHTML = if (rows.isEmpty()) "<div class=\"kb-combo-empty\">No projects match.</div>"
            else rows.joinToString("") { "<button type=\"button\" role=\"option\" class=\"kb-combo-option\" aria-selected=\"${it.id == chosen}\" data-kb-option=\"${escape(it.id)}\">${escape(field(it.data, "title"))}</button>" }
        val nodes = document.querySelectorAll("[data-kb-option]")
        for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let { node -> node.addEventListener("click", { projectPicked(node.getAttribute("data-kb-option")!!) }) }
    }

    private fun confirmDelete(title: String, message: String, onConfirm: () -> Unit) {
        confirmModal(title, message, "kb-confirm", "Delete")
        click("kb-confirm") { onConfirm() }
    }
}
